/**
 * Användaraktivitet: the activity log at `activity/{YYYY-MM-DD}/{uid}/{entryKey}`.
 *
 * The catalogue (entities and verbs) is shared with mf-functions
 * `functions/src/shared/activity.ts` and iOS `Husappen/Helpers/ActivityRecorder.swift` —
 * change all three together. The Swedish lines are rendered here only (iOS
 * writes, it never shows the log).
 */

export const ACTIVITY_ENTITIES = [
    'session', 'building', 'component', 'level', 'room', 'drawing', 'photo', 'document',
    'unit', 'parkingArea', 'parkingSpot', 'storageUnit', 'workOrder', 'issue', 'project',
    'note', 'todo', 'inspection', 'maintenancePlan', 'maintenanceTemplate', 'lease', 'tenant',
    'parkingQueue', 'keyType', 'key', 'user', 'externalUser', 'settings', 'report',
    'operatingCost', 'timelineItem', 'serviceLog',
] as const;
export type ActivityEntity = typeof ACTIVITY_ENTITIES[number];

export const ACTIVITY_VERBS = [
    'started', 'created', 'updated', 'deleted', 'archived', 'completed', 'uploaded',
    'activated', 'converted', 'handedOut', 'returned', 'anonymized', 'imported',
] as const;
export type ActivityVerb = typeof ACTIVITY_VERBS[number];

/** `{entity}.{verb}`, e.g. `workOrder.completed`. */
export type ActivityAction = `${ActivityEntity}.${ActivityVerb}`;

export type ActivityPlatform = 'web' | 'ios' | 'guest' | 'public';

/** The uid key that public QR reports are logged under. */
export const PUBLIC_ACTIVITY_ID = '_public';

export interface ActivityEntry {
    key: string;
    /** Date key the entry is stored under (`YYYY-MM-DD`). */
    date: string;
    /** Firebase UID, externalUserId (guest) or `_public`. */
    actorId: string;
    when: string;
    platform: ActivityPlatform;
    action: string;
    buildingKey?: string;
    targetKey?: string;
    /** Name of a thing, never of a person. */
    label?: string;
    /** > 1 when repeats were collapsed into this entry. */
    count: number;
}

export interface ActivityDetails {
    buildingKey?: string;
    targetKey?: string;
    /** Name of a component, work order, project, room or building. Never a person's name. */
    label?: string;
}

const VERB_TEXT: Record<ActivityVerb, string> = {
    started: 'Startade',
    created: 'Skapade',
    updated: 'Ändrade',
    deleted: 'Tog bort',
    archived: 'Arkiverade',
    completed: 'Slutförde',
    uploaded: 'Laddade upp',
    activated: 'Aktiverade',
    converted: 'Konverterade',
    handedOut: 'Lämnade ut',
    returned: 'Tog emot',
    anonymized: 'Anonymiserade',
    imported: 'Importerade',
};

const ENTITY_TEXT: Record<ActivityEntity, string> = {
    session: 'session',
    building: 'byggnad',
    component: 'komponent',
    level: 'plan',
    room: 'rum',
    drawing: 'ritning',
    photo: 'foto',
    document: 'dokument',
    unit: 'lägenhet/lokal',
    parkingArea: 'parkeringsområde',
    parkingSpot: 'parkeringsplats',
    storageUnit: 'förråd',
    workOrder: 'ärende',
    issue: 'felanmälan',
    project: 'projekt',
    note: 'anteckning',
    todo: 'att göra-punkt',
    inspection: 'kontroll',
    maintenancePlan: 'underhållsplan',
    maintenanceTemplate: 'underhållsmall',
    lease: 'hyresavtal',
    tenant: 'hyresgäst',
    parkingQueue: 'köplats i parkeringskön',
    keyType: 'nyckeltyp',
    key: 'nyckel',
    user: 'användare',
    externalUser: 'extern användare',
    settings: 'inställningar',
    report: 'rapport',
    operatingCost: 'driftkostnad',
    timelineItem: 'tidslinjehändelse',
    serviceLog: 'tjänstelogg',
};

const SPECIAL_TEXT: Partial<Record<string, string>> = {
    'session.started': 'Öppnade appen',
    'key.handedOut': 'Lämnade ut nyckel',
    'key.returned': 'Tog emot återlämnad nyckel',
};

export function isActivityAction(value: string): value is ActivityAction {
    const [entity, verb, extra] = value.split('.');
    return extra === undefined
        && (ACTIVITY_ENTITIES as readonly string[]).includes(entity)
        && (ACTIVITY_VERBS as readonly string[]).includes(verb);
}

/**
 * The Swedish line for an entry, e.g. "Slutförde ärende »Byt filter«".
 * An unknown code (a newer client) is shown as the raw code.
 */
export function describeActivity(entry: Pick<ActivityEntry, 'action' | 'label' | 'count'>): string {
    const special = SPECIAL_TEXT[entry.action];
    let text: string;
    if (special) {
        text = special;
    } else if (isActivityAction(entry.action)) {
        const [entity, verb] = entry.action.split('.') as [ActivityEntity, ActivityVerb];
        text = `${VERB_TEXT[verb]} ${ENTITY_TEXT[entity]}`;
    } else {
        text = entry.action;
    }
    if (entry.label) text += ` »${entry.label}«`;
    if (entry.count > 1) text += ` (${entry.count} gånger)`;
    return text;
}

export const PLATFORM_TEXT: Record<ActivityPlatform, string> = {
    web: 'Webb',
    ios: 'iOS',
    guest: 'Gästlänk',
    public: 'Publik felanmälan',
};
